import type { RefObject } from 'react';
import type { GridApi } from 'ag-grid-community';
import type { GridScrollAnchor } from '../store/tabViewStateStore';

export type AgGridScrollEdge = 'top' | 'bottom' | 'middle';

export type AgGridRowScrollAnchor = {
  rowId: string;
  offset: number;
};

export function getAgGridViewport(
  containerRef?: RefObject<HTMLElement | null>,
): HTMLElement | null {
  const root = containerRef?.current;
  if (!root) return null;
  return root.querySelector<HTMLElement>('.ag-body-viewport');
}

export function detectAgGridScrollEdge(
  viewport: HTMLElement,
): AgGridScrollEdge {
  const { scrollTop, scrollHeight, clientHeight } = viewport;
  if (scrollTop === 0) return 'top';
  if (scrollHeight - scrollTop - clientHeight < 2) return 'bottom';
  return 'middle';
}

export function captureAgGridScrollAnchor<TRowData>({
  gridApi,
  containerRef,
}: {
  gridApi: GridApi<TRowData>;
  containerRef?: RefObject<HTMLElement | null>;
}): GridScrollAnchor | null {
  const viewport = getAgGridViewport(containerRef);
  if (!viewport) return null;

  const firstRowIndex = gridApi.getFirstDisplayedRowIndex();
  return {
    scrollTop: Math.max(0, viewport.scrollTop),
    firstRowIndex:
      firstRowIndex === null || firstRowIndex === undefined || firstRowIndex < 0
        ? 0
        : firstRowIndex,
  };
}

export function restoreAgGridScrollAnchor<TRowData>({
  gridApi,
  containerRef,
  anchor,
}: {
  gridApi: GridApi<TRowData>;
  containerRef?: RefObject<HTMLElement | null>;
  anchor: GridScrollAnchor;
}): boolean {
  const viewport = getAgGridViewport(containerRef);
  if (!viewport || anchor.scrollTop <= 0) return false;

  if (anchor.firstRowIndex > 0) {
    gridApi.ensureIndexVisible(anchor.firstRowIndex, 'top');
  }

  const maxScrollable = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight,
  );
  if (maxScrollable <= 0) return false;

  const targetScrollTop = Math.min(anchor.scrollTop, maxScrollable);
  viewport.scrollTop = targetScrollTop;

  return Math.abs(viewport.scrollTop - targetScrollTop) < 2;
}

export function captureAgGridRowScrollAnchor<TRowData extends { id: string }>({
  gridApi,
  containerRef,
}: {
  gridApi: GridApi<TRowData>;
  containerRef?: RefObject<HTMLElement | null>;
}): AgGridRowScrollAnchor | null {
  const viewport = getAgGridViewport(containerRef);
  if (!viewport) return null;

  const firstRowIndex = gridApi.getFirstDisplayedRowIndex();
  if (
    firstRowIndex === null ||
    firstRowIndex === undefined ||
    firstRowIndex < 0
  ) {
    return null;
  }

  const firstRowNode = gridApi.getDisplayedRowAtIndex(firstRowIndex);
  const rowTop = firstRowNode?.rowTop;
  const rowId = firstRowNode?.data?.id;
  if (!rowId || typeof rowTop !== 'number') return null;

  return {
    rowId,
    offset: viewport.scrollTop - rowTop,
  };
}

export function restoreAgGridRowScrollAnchor<TRowData>({
  gridApi,
  containerRef,
  anchor,
}: {
  gridApi: GridApi<TRowData>;
  containerRef?: RefObject<HTMLElement | null>;
  anchor: AgGridRowScrollAnchor;
}): boolean {
  const viewport = getAgGridViewport(containerRef);
  const rowNode = gridApi.getRowNode(anchor.rowId);
  const rowTop = rowNode?.rowTop;
  if (!viewport || typeof rowTop !== 'number') return false;

  const maxScrollable = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight,
  );
  if (maxScrollable <= 0) return false;

  const targetScrollTop = Math.min(
    Math.max(0, rowTop + anchor.offset),
    maxScrollable,
  );
  viewport.scrollTop = targetScrollTop;

  return Math.abs(viewport.scrollTop - targetScrollTop) < 2;
}
